import { ArrowRightIcon, DangerCircleIcon, Form, handleApiRequest, Typography } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountProfileJobLocationTypeExcludeChoices,
  AuthAccountProfileJobTypeExcludeChoices,
  UpdateInterestedJobsMutation,
  UpdateInterestedJobsMutationVariables,
  useGetInterestedJobsQuery,
  useUpdateInterestedJobsMutation,
} from "@graphql/generated";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { getInputConfig } from "./formCofig";

export type JobInterestFormData = {
  jobType: AuthAccountProfileJobTypeExcludeChoices[];
  jobLocationType: AuthAccountProfileJobLocationTypeExcludeChoices[];
};

interface JobTypeTabContentProps {
  goToNextTab: () => void;
}

const JobTypeTabContent: React.FC<JobTypeTabContentProps> = ({ goToNextTab }) => {
  const formHandler = useForm<JobInterestFormData>({
    defaultValues: {
      jobType: [],
      jobLocationType: [],
    },
    mode: "onChange",
  });

  const { data: userData, refetch } = useGetInterestedJobsQuery();
  const [updateInterestedJObs, { loading: submitLoading }] = useUpdateInterestedJobsMutation();

  const handleSubmit = useCallback(
    async (data: JobInterestFormData) => {
      if (!data.jobType || data.jobType.length === 0) {
        formHandler.setError("jobType", {
          type: "required",
          message: "Please select at least one job type",
        });
        return;
      }

      if (!data.jobLocationType || data.jobLocationType.length === 0) {
        formHandler.setError("jobLocationType", {
          type: "required",
          message: "Please select at least one job location type",
        });
        return;
      }

      await handleApiRequest<UpdateInterestedJobsMutation, UpdateInterestedJobsMutationVariables>({
        apiFunction: updateInterestedJObs,
        variables: data,
        componentName: "JobTypePreferencesForm",
        actionName: "updateJobType",
        successMessage: "Job type updated successfully",
        fallbackErrorMessage: "Failed to update job type preferences. Please try again.",
        onSuccess: () => {
          refetch();
          goToNextTab();
        },
      });
    },
    [formHandler, goToNextTab, refetch, updateInterestedJObs],
  );

  useEffect(() => {
    if (!userData?.me?.profile) return;

    const { jobType, jobLocationType } = userData.me.profile;

    formHandler.reset(
      {
        jobType: Array.isArray(jobType)
          ? jobType.filter((item): item is AuthAccountProfileJobTypeExcludeChoices => item !== null)
          : [],
        jobLocationType: Array.isArray(jobLocationType)
          ? jobLocationType.filter((item): item is AuthAccountProfileJobLocationTypeExcludeChoices => item !== null)
          : [],
      },
      {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false,
      },
    );
  }, [formHandler, userData]);

  return (
    <div className="pt-3 flex flex-col gap-4">
      <div className="flex flex-row gap-2 mb-2 items-center border-1 border-yellow-600 p-2 rounded-lg">
        <div>
          <DangerCircleIcon className="stroke-yellow-600" width={24} height={24} />
        </div>
        <div>
          <Typography className="text-sm text-yellow-600">
            If there are any Job Types or Job Location Types you don't want, simply deselect them and click Save & Next
            to update your preferences.
          </Typography>
        </div>
      </div>
      <Form
        wrapInCard={false}
        formHandler={formHandler as any}
        inputConfigs={getInputConfig(formHandler as any)}
        showCancel={false}
        className="mt-0"
        submitProps={{
          onClick: formHandler.handleSubmit(handleSubmit),
          isLoading: submitLoading,
          isDisabled: submitLoading,
          children: "Save & Next",
          endContent: <ArrowRightIcon stroke="#fff" className="w-4 h-4" />,
        }}
      />
    </div>
  );
};

export default JobTypeTabContent;
